import { useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { useGenerateQuizMutation, useResolveExerciseMutation } from "redux_dir/features/homeworkHelpApi";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { getParentForm, setParentForm } from "redux_dir/features/parentSlice";
import { getCurrentEleve } from "redux_dir/features/eleveSlice";
import { useUpdateParentMutation } from "redux_dir/features/parentApi";
import { ENABLE_CREDIT_SYSTEM } from "constants";

// ===========================================
// CONSTANTES
// ===========================================
export const STEPS = {
    SELECT_TYPE: "SELECT_TYPE",
    INSTRUCTIONS: "INSTRUCTIONS",
    UPLOAD: "UPLOAD",
    PREVIEW: "PREVIEW",
    PROCESSING: "PROCESSING",
};

export const TYPES = {
    QUIZ: "quiz",
    RESOLUTION: "resolution",
};

// ============================================
// ÉTAT INITIAL
// ============================================
const initialState = {
    step: STEPS.SELECT_TYPE,
    selectedType: null,
    selectedImages: [], // Tableau d'images
    selectedFiles: [], // Tableau de fichiers
};

// ============================================
// REDUCER
// ============================================
function homeworkReducer(state, action) {
    switch (action.type) {
        case "SELECT_TYPE":
            return {
                ...state,
                step: STEPS.INSTRUCTIONS,
                selectedType: action.payload,
            };

        case "CONTINUE_FROM_INSTRUCTIONS":
            return {
                ...state,
                step: STEPS.UPLOAD,
            };

        case "SELECT_IMAGE":
            return {
                ...state,
                step: STEPS.PREVIEW,
                selectedImages: [...state.selectedImages, action.payload.image],
                selectedFiles: [...state.selectedFiles, action.payload.file],
            };

        case "REMOVE_IMAGE":
            return {
                ...state,
                selectedImages: state.selectedImages.filter((_, index) => index !== action.payload),
                selectedFiles: state.selectedFiles.filter((_, index) => index !== action.payload),
            };

        case "START_PROCESSING":
            return {
                ...state,
                step: STEPS.PROCESSING,
            };

        case "FINISH_PROCESSING":
            return {
                ...state,
                step: STEPS.PREVIEW,
            };

        case "RESET":
            return initialState;

        default:
            return state;
    }
}

// ============================================
// HELPER
// ============================================
export const getStepTitle = (step) => {
    const titles = {
        [STEPS.SELECT_TYPE]: "Choisissez une option pour commencer :",
        [STEPS.INSTRUCTIONS]: "Suivez les étapes pour photographier vos pages :",
        [STEPS.UPLOAD]: "Sélectionnez les photos de votre exercice (dans l'ordre) :",
        [STEPS.PREVIEW]: "Vérifiez l'ordre de vos photos et lancez le traitement :",
        [STEPS.PROCESSING]: "Traitement en cours...",
    };
    return titles[step] || "";
};

// ============================================
// NOUVELLE FONCTION : Normaliser le fichier
// ============================================
const normalizeImageFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Créer un canvas pour redimensionner/optimiser
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                
                // Limiter la taille max pour réduire le poids
                const MAX_SIZE = 1920;
                if (width > MAX_SIZE || height > MAX_SIZE) {
                    if (width > height) {
                        height = (height / width) * MAX_SIZE;
                        width = MAX_SIZE;
                    } else {
                        width = (width / height) * MAX_SIZE;
                        height = MAX_SIZE;
                    }
                }
                
                canvas.width = width;
                canvas.height = height;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convertir en Blob avec compression JPEG
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            // Créer un nouveau File à partir du Blob
                            const normalizedFile = new File(
                                [blob], 
                                file.name || 'exercise.jpg',
                                { type: 'image/jpeg' }
                            );
                            resolve(normalizedFile);
                        } else {
                            reject(new Error('Échec de la conversion en Blob'));
                        }
                    },
                    'image/jpeg',
                    0.85 // Qualité de compression
                );
            };
            
            img.onerror = () => reject(new Error('Échec du chargement de l\'image'));
            img.src = e.target.result;
        };
        
        reader.onerror = () => reject(new Error('Échec de la lecture du fichier'));
        reader.readAsDataURL(file);
    });
};

// ============================================
// HOOK PRINCIPAL
// ============================================
export default function useHomeworkHelp() {
    const navigation = useNavigate();
    const student = useSelector(getCurrentEleve);
    const reduxDispatch = useDispatch();
    const formParent = useSelector(getParentForm);

    const [state, dispatch] = useReducer(homeworkReducer, initialState);
    const { step, selectedType, selectedImages, selectedFiles } = state;

    const [generateQuiz] = useGenerateQuizMutation();
    const [resolveExercise] = useResolveExerciseMutation();
    const [updateParentCredit] = useUpdateParentMutation();

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // ============================================
    // HANDLERS
    // ============================================
    const handleTypeSelection = (type) => {
        dispatch({ type: "SELECT_TYPE", payload: type });
    };

    const handleContinueFromInstructions = () => {
        dispatch({ type: "CONTINUE_FROM_INSTRUCTIONS" });
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        try {
            // Pour la résolution, limiter à une seule image
            if (selectedType === TYPES.RESOLUTION && selectedImages.length >= 1) {
                toast.warning("La résolution d'exercice n'accepte qu'une seule image pour le moment.");
                event.target.value = '';
                return;
            }

            // Traiter toutes les images sélectionnées
            for (const file of files) {
                // Pour la résolution, ne traiter que la première image
                if (selectedType === TYPES.RESOLUTION && selectedImages.length >= 1) {
                    break;
                }

                const normalizedFile = await normalizeImageFile(file);
                const imageUrl = URL.createObjectURL(normalizedFile);
                
                dispatch({
                    type: "SELECT_IMAGE",
                    payload: { 
                        image: imageUrl, 
                        file: normalizedFile 
                    },
                });
                
                // Libérer la mémoire après un délai
                setTimeout(() => URL.revokeObjectURL(imageUrl), 30000);
            }
            
            // Réinitialiser l'input pour permettre de resélectionner les mêmes fichiers
            event.target.value = '';
            
        } catch (error) {
            console.error("Image upload error:", error);
            toast.error("Erreur lors du traitement de l'image. Veuillez réessayer.");
        }
    };

    const handleRemoveImage = (index) => {
        dispatch({ type: "REMOVE_IMAGE", payload: index });
    };

    // --- Déduction de crédits (désactivable via ENABLE_CREDIT_SYSTEM) ---
    const updateCredits = async () => {
        if (!ENABLE_CREDIT_SYSTEM) return; // Système de crédits désactivé
        if (formParent?.credits > 0) {
            const resStatus = await updateParentCredit({
                parentId: formParent?.id,
                credits: formParent?.credits - 25,
            });
            if (resStatus?.data) {
                reduxDispatch(setParentForm({
                    ...formParent,
                    credits: resStatus?.data?.data?.credits,
                }));
            }
        }
    };

    const handleSubmit = async () => {
        if (!selectedFiles.length) {
            toast.error("Veuillez sélectionner au moins une image");
            return;
        }

        console.log('🚀 [SUBMIT] Début de la soumission');
        console.log('📋 [SUBMIT] Student:', student);
        console.log('🆔 [SUBMIT] Student ID:', student?.id);
        console.log('📝 [SUBMIT] Type d\'activité:', selectedType);

        dispatch({ type: "START_PROCESSING" });

        try {
            // Log pour debugging
            console.log("Fichiers envoyés:", selectedFiles.map(f => ({
                name: f.name,
                type: f.type,
                size: f.size,
            })));

            if (selectedType === TYPES.QUIZ) {
                // Quiz : envoyer un tableau (ou un seul fichier si une seule image)
                const images = selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles;
                console.log("Quiz - Payload type:", Array.isArray(images) ? "Array" : "File");
                
                console.log('📤 [QUIZ] Appel generateQuiz avec:', { 
                    hasImages: !!images, 
                    studentId: student?.id 
                });
                
                const result = await generateQuiz({ 
                    images, 
                    studentId: student?.id 
                }).unwrap();
                
                console.log('✅ [QUIZ] Résultat reçu:', result);
                
                navigation(`/${student?.id}/homework/quiz`, {
                    state: { quizData: result, eleveId: student?.id },
                });
            } else {
                // Résolution : envoyer uniquement la première image
                const image = selectedFiles[0];
                console.log("Résolution - Payload type: File");
                
                console.log('📤 [RESOLUTION] Appel resolveExercise avec:', { 
                    hasImage: !!image, 
                    studentId: student?.id 
                });
                
                const result = await resolveExercise({ 
                    image, 
                    studentId: student?.id 
                }).unwrap();
                
                console.log('✅ [RESOLUTION] Résultat reçu:', result);
                
                navigation(`/${student?.id}/homework/resolution`, {
                    state: { resolutionData: result, eleveId: student?.id },
                });
            }
            await updateCredits();
        } catch (error) {
            console.error("Error détaillé:", error);
            console.error("Error response:", error?.data);
            
            // Vérifier si c'est une erreur d'indisponibilité de l'IA
            if (error?.status === 503 || error?.data?.error === "AI_UNAVAILABLE") {
                toast.error("Assistant IA momentanément indisponible", {
                    duration: 5000,
                    style: {
                        background: '#ef4444',
                        color: '#fff',
                    },
                });
            } else {
                toast.error(error?.data?.message || "Erreur lors du traitement. Veuillez vous assurer que les photos sont claires et bien cadrées.");
            }
            
            dispatch({ type: "FINISH_PROCESSING" });
        }
    };

    const handleReset = () => {
        dispatch({ type: "RESET" });
    };

    // ============================================
    // RETOUR DU HOOK
    // ============================================
    return {
        step,
        selectedType,
        selectedImages,
        selectedFiles,
        isIOS,
        student,
        handleTypeSelection,
        handleContinueFromInstructions,
        handleImageUpload,
        handleRemoveImage,
        handleSubmit,
        handleReset,
    };
}